import {
  addDays,
  comparePositions,
  matchMember,
  parseQuickAdd,
  positionBetween,
  TASK_STATUSES,
  type Label,
  type Member,
  type TaskStatus,
} from '@hive/shared';
import type { GridTask } from '@/data/tasks';

// What Grid's list and board show, worked out from the game's tasks. Pure, so
// it's unit-tested without Supabase.

export type DueFilter = 'any' | 'overdue' | 'today' | 'week' | 'none';
export type SortKey = 'manual' | 'due' | 'priority' | 'newest' | 'title';

export interface GridFilters {
  /** Member id, 'unassigned', or null for anyone. */
  assignee: string | null;
  labelId: string | null;
  priority: number | null;
  due: DueFilter;
  text: string;
  /** A sprint id, 'backlog' (no sprint), or null for any. Grid turns "current" into an id. */
  sprint: string | null;
}

export const NO_FILTERS: GridFilters = {
  assignee: null,
  labelId: null,
  priority: null,
  due: 'any',
  text: '',
  sprint: null,
};

export function hasFilters(f: GridFilters): boolean {
  return (
    f.assignee !== null ||
    f.labelId !== null ||
    f.priority !== null ||
    f.due !== 'any' ||
    f.text.trim() !== '' ||
    f.sprint !== null
  );
}

export function matchesFilters(task: GridTask, f: GridFilters, today: string): boolean {
  if (f.assignee === 'unassigned' ? task.assignee_id !== null : f.assignee && task.assignee_id !== f.assignee) {
    return false;
  }
  if (f.labelId && !task.label_ids.includes(f.labelId)) return false;
  if (f.priority && task.priority !== f.priority) return false;
  if (f.sprint === 'backlog' ? task.sprint_id !== null : f.sprint && task.sprint_id !== f.sprint) return false;
  switch (f.due) {
    case 'overdue':
      if (!task.due_date || task.due_date >= today || task.status === 'done') return false;
      break;
    case 'today':
      if (task.due_date !== today) return false;
      break;
    case 'week':
      if (!task.due_date || task.due_date > addDays(today, 7)) return false;
      break;
    case 'none':
      if (task.due_date) return false;
      break;
  }
  const text = f.text.trim().toLowerCase();
  if (text) {
    const haystack = `${task.title}\n${task.description}`.toLowerCase();
    if (!text.split(/\s+/).every((word) => haystack.includes(word))) return false;
  }
  return true;
}

const byPosition = (a: GridTask, b: GridTask) => comparePositions(a.position, b.position) || a.id.localeCompare(b.id);

export function sortTasks(tasks: readonly GridTask[], sort: SortKey): GridTask[] {
  const list = [...tasks];
  switch (sort) {
    case 'manual':
      return list.sort(byPosition);
    case 'due':
      // Dated tasks first, soonest first; undated ones keep manual order.
      return list.sort((a, b) =>
        a.due_date && b.due_date
          ? a.due_date.localeCompare(b.due_date) || byPosition(a, b)
          : a.due_date
            ? -1
            : b.due_date
              ? 1
              : byPosition(a, b),
      );
    case 'priority':
      return list.sort((a, b) => a.priority - b.priority || byPosition(a, b));
    case 'newest':
      return list.sort((a, b) => b.created_at.localeCompare(a.created_at));
    case 'title':
      return list.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
  }
}

export type Columns = Record<TaskStatus, GridTask[]>;

/**
 * Top-level tasks matching the filters, by status. Done tasks come newest
 * completion first when sorting manually, so years of history don't bury today.
 */
export function groupByStatus(tasks: readonly GridTask[], f: GridFilters, sort: SortKey, today: string): Columns {
  const columns = Object.fromEntries(TASK_STATUSES.map((s) => [s.id, [] as GridTask[]])) as Columns;
  for (const task of tasks) {
    if (task.parent_id === null && matchesFilters(task, f, today)) columns[task.status].push(task);
  }
  for (const s of TASK_STATUSES) {
    columns[s.id] =
      s.id === 'done' && sort === 'manual'
        ? [...columns.done].sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''))
        : sortTasks(columns[s.id], sort);
  }
  return columns;
}

/** Subtasks by parent id, in position order. */
export function subtasksByParent(tasks: readonly GridTask[]): Map<string, GridTask[]> {
  const map = new Map<string, GridTask[]>();
  for (const t of [...tasks].sort(byPosition)) {
    if (!t.parent_id) continue;
    const list = map.get(t.parent_id) ?? [];
    list.push(t);
    map.set(t.parent_id, list);
  }
  return map;
}

export interface QuickAddPlan {
  title: string;
  priority: number | null;
  due: string | null;
  assignee: Member | null;
  /** The @name that matched nobody, to warn about. */
  unknownAssignee: string | null;
  /** Labels that already exist for this game. */
  labels: Label[];
  /** #names with no label yet; quick-add creates them in this game. */
  newLabels: string[];
}

/** Reads a quick-add line and matches its @name and #labels against the studio. */
export function planQuickAdd(
  input: string,
  members: readonly Member[],
  gameLabels: readonly Label[],
  now = new Date(),
): QuickAddPlan {
  const parsed = parseQuickAdd(input, now);
  const assignee = parsed.assignee ? matchMember(parsed.assignee, members) : null;
  const labels: Label[] = [];
  const newLabels: string[] = [];
  for (const name of parsed.labels) {
    // A game's own label wins over a studio-wide one of the same name.
    const found =
      gameLabels.find((l) => l.game_id && l.name.toLowerCase() === name.toLowerCase()) ??
      gameLabels.find((l) => l.name.toLowerCase() === name.toLowerCase());
    if (found) labels.push(found);
    else newLabels.push(name);
  }
  return {
    title: parsed.title,
    priority: parsed.priority,
    due: parsed.due,
    assignee,
    unknownAssignee: parsed.assignee && !assignee ? parsed.assignee : null,
    labels,
    newLabels,
  };
}

export interface DropTarget {
  /** The column dropped on, when dropped on its empty space. */
  column?: TaskStatus;
  /** The card dropped on. */
  taskId?: string;
}

/**
 * Where a dragged card lands: its new status, and a new position when the board
 * is in manual order (other sorts only change status). Null for no change.
 */
export function planDrop(
  columns: Columns,
  activeId: string,
  target: DropTarget,
  sort: SortKey,
): { status: TaskStatus; position?: string } | null {
  const from = TASK_STATUSES.find((s) => columns[s.id].some((t) => t.id === activeId))?.id;
  if (!from) return null;
  const to = target.column ?? TASK_STATUSES.find((s) => columns[s.id].some((t) => t.id === target.taskId))?.id;
  if (!to) return null;

  // Done is ordered by completion date, and other sorts aren't manual: only the status moves.
  if (sort !== 'manual' || to === 'done') return from === to ? null : { status: to };

  const ids = columns[to].map((t) => t.id);
  const keyOf = new Map(columns[to].map((t) => [t.id, t.position]));
  let order: string[];
  if (from === to) {
    const oldIndex = ids.indexOf(activeId);
    const newIndex = target.taskId ? ids.indexOf(target.taskId) : ids.length - 1;
    if (newIndex < 0 || oldIndex === newIndex) return null;
    order = ids.filter((id) => id !== activeId);
    order.splice(newIndex, 0, activeId);
  } else {
    const at = target.taskId ? ids.indexOf(target.taskId) : -1;
    order = [...ids];
    order.splice(at < 0 ? ids.length : at, 0, activeId);
  }
  const i = order.indexOf(activeId);
  const before = order[i - 1];
  const after = order[i + 1];
  return {
    status: to,
    position: positionBetween(before ? keyOf.get(before) : null, after ? keyOf.get(after) : null),
  };
}
